// A steam room's finishes in the 3D view (docs/claude/woods.md, *Steam room
// finishes*): each finish's surfaces generated as textures (finishTextures.ts),
// and the room's meshes dressed in them — a wall panel's skins inside and out,
// its core between them where it is cut across, the ceiling's underside and the
// roof — through the same stand-ins and `split` as a sauna's woods.

import * as THREE from 'three';
import type {
  ProductInstance,
  Vec2,
} from '../../shared/types';
import {
  FINISHES,
  type FinishId,
  roomFinishOf,
} from '../../shared/finishes';
import {
  cutUVs,
  panelFace,
} from '../finishFaces';
import {
  cutRaster,
  PERIOD,
  type Raster,
  rasterize,
  surfaceField,
} from '../finishTextures';
import {
  announceTexture,
  type Cladding,
  type DressAt,
  split,
  type Surface,
} from './woods3d';
import { withLook } from './sceneNodes';

/** Pixels to the mm of a generated face, and of a panel cut across (its beads
 *  and 2 mm skins want the finer). */
const FACE_PX = 4;
const CUT_PX = 7;

/** A raster as a repeating texture. Kept for the session (`made`): a material's
 *  disposal leaves its map. */
function textureOf(r: Raster, repeatS: boolean): THREE.DataTexture {
  const t = new THREE.DataTexture(r.data, r.width, r.height, THREE.RGBAFormat);
  t.wrapS = repeatS ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  t.needsUpdate = true;
  return t;
}

/** Each texture made, by its key, and the materials waiting for one being made. */
const made = new Map<string, THREE.DataTexture>();
const waiting = new Map<string, THREE.MeshStandardMaterial[]>();

/** A material in a generated texture, in its recipe's average colour until the
 *  texture is made — after the scene is built, so the first view of a steam
 *  room does not wait on it — and in the exports. None is made in node, where
 *  the GLB export could not encode it. */
function generatedMaterial(average: string, key: string, make: () => THREE.DataTexture): THREE.MeshStandardMaterial {
  const avg = new THREE.Color(average);
  const m = withLook(new THREE.MeshStandardMaterial({ color: avg.clone(), roughness: 0.95, metalness: 0, side: THREE.DoubleSide }), 'solid', avg);
  if (typeof document === 'undefined') return m;
  const dress = (t: THREE.Texture): void => { m.map = t; m.color.setScalar(1); m.needsUpdate = true; };
  const had = made.get(key);
  if (had) { dress(had); return m; }
  const list = waiting.get(key);
  if (list) { list.push(m); return m; }
  waiting.set(key, [m]);
  setTimeout(() => {
    const t = make();
    made.set(key, t);
    for (const w of waiting.get(key) ?? []) { w.map = t; w.color.setScalar(1); w.needsUpdate = true; }
    waiting.delete(key);
    announceTexture();
  }, 0);
  return m;
}

/** A finish's face as a surface: its texture, a period of it to `PERIOD` mm. */
function faceSurface(id: FinishId): Surface {
  const r = FINISHES[id].face;
  const mat = generatedMaterial(r.average, `${id}:face`, () => textureOf(rasterize(surfaceField(r), FACE_PX), true));
  return { mat, img: { w: PERIOD, h: PERIOD, boards: 1 }, turn: false };
}

/** A material for a panel `thickness` thick cut across: across the texture its
 *  thickness, outside to inside, the core between a skin of the face at each
 *  side; down it a `PERIOD`. */
function cutMaterial(id: FinishId, thickness: number): THREE.MeshStandardMaterial {
  const f = FINISHES[id], core = f.core!;
  return generatedMaterial(core.surface.average, `${id}:cut:${thickness}`,
    () => textureOf(cutRaster(surfaceField(core.surface), surfaceField(f.face), core.skin, thickness, CUT_PX), false));
}

/**
 * The steam room's finishes as its cladding, or null when every part is in its
 * colour. `colour` holds the materials of its colours; `outer` is its outer
 * outline (local) and `thickness` its walls', across which a cut face shows the
 * inside finish's core — the inside's face where it has none.
 */
export function finishCladding(
  inst: ProductInstance,
  colour: { wall: THREE.Material; ceil: THREE.Material },
  outer: readonly Vec2[],
  thickness: number,
  axes: { u: Vec2; n: Vec2 },
): Cladding | null {
  const fIn = roomFinishOf(inst.finishes, 'wallsIn'), fOut = roomFinishOf(inst.finishes, 'wallsOut'), fCeil = roomFinishOf(inst.finishes, 'ceiling');
  if (!fIn && !fOut && !fCeil) return null;
  const faces = new Map<FinishId, Surface>();
  const faceOf = (id: FinishId): Surface => { let s = faces.get(id); if (!s) faces.set(id, s = faceSurface(id)); return s; };
  const plain = (mat: THREE.Material): Surface => ({ mat, img: null, turn: false });
  const inside = fIn ? faceOf(fIn) : plain(colour.wall), outside = fOut ? faceOf(fOut) : plain(colour.wall);
  const ceiling = fCeil ? faceOf(fCeil) : plain(colour.ceil);
  // The roof (the slab's top and edges, a shaped shell's outer surface and
  // fascia) is the outside's finish, or the ceiling's colour.
  const top = fOut ? outside : plain(colour.ceil);
  const coreId = fIn && FINISHES[fIn].core ? fIn : null;
  const cut: Surface = coreId && thickness > 0
    ? { mat: cutMaterial(coreId, thickness), img: null, turn: false, uvs: (tri, n) => cutUVs(outer, thickness, tri, n, PERIOD) }
    : inside;
  const stand = (name: string): THREE.Material => { const m = new THREE.MeshBasicMaterial(); m.name = name; return m; };
  const wall = stand('wall'), ceil = stand('ceiling'), roof = stand('roof');
  // The benches (and backrests) are built of the walls' panels: in the inside's
  // finish, or their own colours while the inside has none.
  const bench = fIn ? stand('bench') : null;
  const side = { in: inside, out: outside, cut };
  const dress = (m: THREE.Mesh, at: DressAt): THREE.Mesh[] => {
    const mat = m.material as THREE.Material;
    if (mat === wall) return split(m, inst, (c, n) => side[panelFace(outer, thickness, c, n)], axes, false);
    if (mat === bench) return split(m, inst, () => inside, axes, at.ownUV);
    if (mat === roof) return split(m, inst, () => top, axes, at.ownUV);
    if (mat === ceil) {
      // The flat slab: its underside is the ceiling, the rest of it the roof.
      if (!at.ownUV && at.ceiling) return split(m, inst, (_c, n) => (n[2] < -0.7 ? ceiling : top), axes, false);
      return split(m, inst, () => ceiling, axes, at.ownUV);
    }
    return [m];
  };
  return { wall, ceil, roof, bench: bench ? () => bench : null, grille: null, dress };
}
